<template>
  <div v-if="formResponse" class="booking-summary">

    <!--  Before filling in form  -->
    <div v-if="!formResponse.completed && !submitted">

      <!--  Welcome message  -->
      <div v-if="formResponse.welcomeMessage" v-bind:inner-html.prop="formResponse.welcomeMessage | marked" class="booking-summary__welcome"></div>

      <!--  Overview details  -->
      <table v-if="formResponse.showBookingSummary" class="booking-summary__summary">
        <thead>
        <th colspan="2"><h4>Booking Summary</h4></th>
        </thead>
        <tr>
          <td>Booked by:</td>
          <td>{{formResponse.booking.bookedBy.firstName}} {{formResponse.booking.bookedBy.lastName}}</td>
        </tr>
        <tr>
          <td>Check-in:</td>
          <td>
            <template v-if="isAmendingBooking">
              Pending confirmation from host
            </template>
            <template v-else>
              {{formatDate(formResponse.booking.arrival)}}
              <div v-if="formResponse.booking.initialArrival && formResponse.booking.arrival !== formResponse.booking.initialArrival"
                   class="booking-summary__summary--amended">
                <span v-if="isAmendingBooking" class="booking-summary__summary-amending-star">*</span>
                <s v-bind:class="{'booking-summary__summary--amending' : isAmendingBooking}">
                  <span>&nbsp;{{formatDate(formResponse.booking.initialArrival)}}&nbsp;</span>
                </s>
              </div>
            </template>
          </td>
        </tr>
        <tr>
          <td>Check-out:</td>
          <td>
            <template v-if="isAmendingBooking">
              Pending confirmation from host
            </template>
            <template v-else>
              {{formatDate(formResponse.booking.departure)}}
              <div v-if="formResponse.booking.initialDeparture && formResponse.booking.departure !== formResponse.booking.initialDeparture"
                   class="booking-summary__summary--amended">
                <span v-if="isAmendingBooking" class="booking-summary__summary-amending-star">*</span>
                <s v-bind:class="{'booking-summary__summary--amending' : isAmendingBooking}">
                  <span>&nbsp;{{formatDate(formResponse.booking.initialDeparture)}}&nbsp;</span>
                </s>
              </div>
            </template>
          </td>
        </tr>
        <tr>
          <td>Nights:</td>
          <td>
            <template v-if="isAmendingBooking">
              Pending confirmation from host
            </template>
            <template v-else>
              {{numberOfNights}}
              <div v-if="initialNumberOfNights && numberOfNights !== initialNumberOfNights"
                        class="booking-summary__summary--amended">
                <span v-if="isAmendingBooking" class="booking-summary__summary-amending-star">*</span>
                <s v-bind:class="{'booking-summary__summary--amending' : isAmendingBooking}">
                  <span>&nbsp;{{initialNumberOfNights}}&nbsp;</span>
                </s>
              </div>
            </template>
          </td>
        </tr>
        <tr>
          <td>Persons:</td>
          <td>
            <template v-if="isAmendingBooking">
              Pending confirmation from host
            </template>
            <template v-else>
              {{totalPersons}}
              <div v-if="initialTotalPersons && totalPersons !== initialTotalPersons"
                        class="booking-summary__summary--amended">
                <span v-if="isAmendingBooking" class="booking-summary__summary-amending-star">*</span>
                <s v-bind:class="{'booking-summary__summary--amending' : isAmendingBooking}">
                  <span>&nbsp;{{initialTotalPersons}}&nbsp;</span>
                </s>
              </div>
            </template>
          </td>
        </tr>

        <!-- Pricing Section-->
        <template v-if="hasAccommodationPrice">
          <!-- Total made up of parts -->
          <template v-if="hasPricingComponentsOtherThanAccommodation">
            <tr>
              <td>Accommodation:</td>
              <td>
                <template v-if="isAmendingBooking">
                  Pending confirmation from host
                </template>
                <template v-else>
                  <div class="booking-summary__summary__price-currency">
                    {{formResponse.booking.currencyCode}}
                  </div>
                  <div class="booking-summary__summary__price-amount">
                    {{Number(formResponse.booking.total).toLocaleString()}}
                    <div v-if="formResponse.booking.initialTotal && formResponse.booking.initialTotal > 0 && formResponse.booking.total !== formResponse.booking.initialTotal"
                         class="booking-summary__summary--amended">
                      <s>
                        <span>&nbsp;{{Number(formResponse.booking.initialTotal).toLocaleString()}}&nbsp;</span>
                      </s>
                    </div>
                  </div>
                </template>
              </td>
            </tr>

            <tr v-if="hasExtrasTotal">
              <td>Extras:</td>
              <td>
                <div class="booking-summary__summary__price-currency">
                  {{formResponse.booking.currencyCode}}
                </div>
                <div class="booking-summary__summary__price-amount">
                  {{Number(formResponse.booking.extrasTotal).toLocaleString()}}
                </div>
              </td>
            </tr>

            <tr v-if="hasCleaningFeeTotal">
              <td>Cleaning fee:</td>
              <td>
                <div class="booking-summary__summary__price-currency">
                  {{formResponse.booking.currencyCode}}
                </div>
                <div class="booking-summary__summary__price-amount">
                  {{Number(formResponse.booking.cleaningFeeTotal).toLocaleString()}}
                </div>
              </td>
            </tr>

            <tr>
              <td>Total:</td>
              <td>
                <div class="booking-summary__summary__total">
                  <template v-if="isAmendingBooking">
                    Pending confirmation from host
                  </template>
                  <template v-else>
                    <div class="booking-summary__summary__price-currency">
                      {{formResponse.booking.currencyCode}}
                    </div>
                    <div class="booking-summary__summary__price-amount">
                      {{Number(formResponse.booking.totalPrice).toLocaleString()}}
                    </div>
                  </template>
                </div>
              </td>
            </tr>
          </template>

          <!-- Simple Total -->
          <template v-else>
            <tr>
              <td>Total:</td>
              <td>
                <template v-if="isAmendingBooking">
                  Pending confirmation from host
                </template>
                <template v-else>
                  {{formResponse.booking.currencyCode}}
                  {{Number(formResponse.booking.total).toLocaleString()}}
                  <div v-if="formResponse.booking.initialTotal && formResponse.booking.initialTotal > 0 && formResponse.booking.total !== formResponse.booking.initialTotal"
                       class="booking-summary__summary--amended">
                    <s>
                      <span>&nbsp;{{Number(formResponse.booking.initialTotal).toLocaleString()}}&nbsp;</span>
                    </s>
                  </div>
                </template>
              </td>
            </tr>
          </template>

        </template>

        <template v-if="(hasBreakageDeposit && !isAirBnbBooking) || hasAccommodationPrice">
          <tr>
            <td>Deposits:</td>
            <td>
              <template v-if="hasAccommodationPrice">
                <template v-if="isAmendingBooking">
                  Pending confirmation from host
                </template>
                <template v-else>
                  {{formResponse.booking.currencyCode}} {{Number(formResponse.booking.deposit).toLocaleString()}}
                  <template v-if="formResponse.booking.initialDeposit && formResponse.booking.initialDeposit > 0 && formResponse.booking.deposit !== formResponse.booking.initialDeposit"
                            class="booking-summary__summary--amended">
                    <s>
                      <span>&nbsp;{{Number(formResponse.booking.initialDeposit).toLocaleString()}}&nbsp;</span>
                    </s>
                  </template>
                  (booking)
                </template>
              </template>
              <!-- Note: Don't show this for AirBnb bookings as they take all deposits -->
              <template v-if="hasBreakageDeposit && !isAirBnbBooking">
                <template v-if="hasAccommodationPrice">
                  <br/>
                </template>
                {{formResponse.booking.currencyCode}} {{Number(breakageDeposit).toLocaleString()}}
                ({{getUnitTypeForDisplay(formResponse.establishment.preferences.displayCategoryForRoomTypes)}} security, refundable)
              </template>
            </td>
          </tr>
        </template>

        <tr><td class="booking-summary__summary-section-break"></td></tr>

        <tr>
          <td>{{getUnitTypeForDisplay(formResponse.establishment.preferences.displayCategoryForRoomTypes)}}</td>
          <td>
            <table class="booking-summary__summary-rooms">
              <thead>
              <th>Adult<span class="d-none d-sm-inline">s</span><br/><span class="text-note">({{formResponse.establishment.rules.childCategory2AgeLimit}}+<span class="d-none d-sm-inline"> yrs</span>)</span></th>
              <th>Child<span class="d-none d-sm-inline">ren</span><br/><span class="text-note">({{formResponse.establishment.rules.childCategory2AgeFrom}} - {{formResponse.establishment.rules.childCategory2AgeTo}}<span class="d-none d-sm-inline"> yrs</span>)</span></th>
              <th>Child<span class="d-none d-sm-inline">ren</span><br/><span class="text-note">({{formResponse.establishment.rules.childCategory1AgeFrom}} - {{formResponse.establishment.rules.childCategory1AgeTo}}<span class="d-none d-sm-inline"> yrs</span>)</span></th>
              <th v-if="formResponse.showPricing">Daily Rate<br/><span class="text-note">(avg)</span></th>
              </thead>
            </table>
          </td>
        </tr>
        <tr v-for="(room, index) in formResponse.booking.rooms" v-bind:key="index">
          <td class="booking-summary__summary-room-type">
            <b>
              <template v-if="formResponse.establishment.preferences.showRoomTypeNameInsteadOfRoomName">
                {{room.roomTypeName}}
              </template>
              <template v-else>
                {{room.roomName}}
              </template>
            </b>
          </td>
          <td>
            <table class="booking-summary__summary-rooms">
              <tr>
                <td>
                  {{room.numberOfAdults}}
                  <div v-if="room.initialNumberOfAdults && room.numberOfAdults !== room.initialNumberOfAdults"
                            class="booking-summary__summary--amended">
                    <span v-if="isAmendingBooking" class="booking-summary__summary-amending-star">*</span>
                    <s v-bind:class="{'booking-summary__summary--amending' : isAmendingBooking}">
                      <span>&nbsp;{{parseInt(room.initialNumberOfAdults)}}&nbsp;</span>
                    </s>
                  </div>
                </td>
                <td>
                  <template v-if="isChildrenCategory2Allowed(room)">
                    {{parseInt(room.numberOfChildren2)}}
                    <div v-if="room.initialNumberOfChildren2 && room.numberOfChildren2 !== room.initialNumberOfChildren2"
                              class="booking-summary__summary--amended">
                      <span v-if="isAmendingBooking" class="booking-summary__summary-amending-star">*</span>
                      <s v-bind:class="{'booking-summary__summary--amending' : isAmendingBooking}">
                        <span>&nbsp;{{parseInt(room.initialNumberOfChildren2)}}&nbsp;</span>
                      </s>
                    </div>
                  </template>
                  <template v-else>
                    N/A
                  </template>
                </td>
                <td>
                  <template v-if="isChildrenCategory1Allowed(room)">
                    {{parseInt(room.numberOfChildren1)}}
                    <div v-if="room.initialNumberOfChildren1 && room.numberOfChildren1 !== room.initialNumberOfChildren1"
                         class="booking-summary__summary--amended">
                      <span v-if="isAmendingBooking" class="booking-summary__summary-amending-star">*</span>
                      <s v-bind:class="{'booking-summary__summary--amending' : isAmendingBooking}">
                        <span>&nbsp;{{parseInt(room.initialNumberOfChildren1)}}&nbsp;</span>
                      </s>
                    </div>
                  </template>
                  <template v-else>
                    N/A
                  </template>
                </td>
                <td v-if="formResponse.showPricing">
                  <template v-if="isAmendingBooking">
                    Pending
                  </template>
                  <template v-else>
                    <span class="d-none d-sm-inline">{{formResponse.booking.currencyCode}}</span> {{Number(room.roomRate).toLocaleString()}}
                    <template v-if="room.initialRoomRate && room.initialRoomRate > 0 && room.roomRate !== room.initialRoomRate">
                      <span class="booking-summary__summary--amended">{{Number(room.initialRoomRate).toLocaleString()}}</span>
                    </template>
                  </template>
                </td>
              </tr>
            </table>
          </td>
        </tr>
        <tr v-if="!isArrivalPassed">
          <td colspan="2" class="text-center">
            <button class="btn btn-secondary" v-on:click.prevent="editGuestAllocation">Request Amendment</button>
          </td>
        </tr>
        <tr v-if="isAmendingBooking">
          <td colspan="2" class="booking-summary__summary__start text-left">
            <span class="booking-summary__summary-amending-star booking-summary__summary-amending-star--narrow">*</span>
            Please note you have requested an amendment to your original booking. This may affect availability and the
            total. Please complete the rest of this form and your host will be in touch to confirm the changes.
          </td>
        </tr>
        <tr><td colspan="2" class="booking-summary__summary__start">
          <input type="checkbox" v-model="allowCheckIn">
          I confirm that the above guest allocation is correct
          <br/>
          <template v-if="!allowCheckIn && warnConfirmGuestAllocation">
            <span class="error-message">Please confirm the guest allocation</span>
            <br>
          </template>
          <button class="btn btn-primary" v-on:click="showForm" v-show="showShowFormButton" v-bind:class="{'btn-disabled': !allowCheckIn}">{{showFormButtonText}}</button>
        </td></tr>
      </table>

      <!--  CTA if no overview -->
      <div v-else  class="booking-summary__no-summary">
        <button class="btn btn-primary" v-on:click="showForm" v-show="showShowFormButton" v-bind:class="{'btn-disabled': !allowCheckIn}">{{showFormButtonText}}</button>
      </div>

    </div>

    <!-- Submitted form-->
    <div v-else-if="!formResponse.completed && submitted" class="booking-summary__thank-you">
      <p>
        Thank you, please be patient while we save your response.
      </p>
      <div class="check-in__loading">
        <Spinner colour="#242F3E"/>
      </div>
    </div>

    <!-- After form submitted -->
    <div v-else class="booking-summary__thank-you">
      <div v-if="formResponse.thankYouMessage" v-bind:inner-html.prop="formResponse.thankYouMessage | marked"></div>
      <p v-else>
        Thank you for completing your form. A copy has been emailed to you.
      </p>
    </div>

    <edit-guest-allocation v-bind:booking="formResponse.booking"
                           v-bind:establishment="formResponse.establishment"
                           v-bind:displayCategoryForRoomTypes="formResponse.establishment.preferences.displayCategoryForRoomTypes"
                           v-if="showEditGuestAllocation">

    </edit-guest-allocation>

  </div>
</template>

<script>
import dayjs from 'dayjs'

import Spinner from '../components/Spinner.vue'
import EditGuestAllocation from '../components/EditGuestAllocation.vue'

import { bookingMixin } from '../mixins/BookingMixin'

export default {
  name: 'BookingSummaryAndMessaging',
  components: { Spinner, EditGuestAllocation },
  mixins: [bookingMixin],
  props: {
    formResponse: Object,
    submitted: Boolean
  },
  data: function () {
    return {
      showEditGuestAllocation: false,
      allowCheckIn: false,
      warnConfirmGuestAllocation: false
    }
  },
  computed: {
    isArrivalPassed: function () {
      const today = dayjs().startOf('day')
      return this.formResponse && today.isAfter(dayjs(this.formResponse.booking.arrival))
    },
    hasAccommodationPrice: function () {
      return this.formResponse.showPricing && this.formResponse.booking.total !== '0.00'
    },
    hasExtrasTotal: function () {
      return this.formResponse.booking.extrasTotal && this.formResponse.booking.extrasTotal !== '0.00'
    },
    hasCleaningFeeTotal: function () {
      return this.formResponse.booking.cleaningFeeTotal && this.formResponse.booking.cleaningFeeTotal !== '0.00'
    },
    hasPricingComponentsOtherThanAccommodation: function () {
      return this.hasExtrasTotal || this.hasCleaningFeeTotal
    },
    hasBreakageDeposit: function () {
      return this.breakageDeposit > 0
    }
  },
  methods: {
    editGuestAllocation: function () {
      this.showEditGuestAllocation = true
    },
    showForm: function () {
      if (this.allowCheckIn) {
        this.$parent.showForm()
      } else {
        this.warnConfirmGuestAllocation = true
      }
    }
  },
  mounted () {
    if (!this.formResponse.showBookingSummary) {
      this.allowCheckIn = true
    }
    this.$root.$on('finished-editing-guest-allocation', () => {
      this.showEditGuestAllocation = false
    })
  }
}
</script>

<style lang="scss" scoped>
  @import "../styles/base/base.scss";
  @import "../styles/components/booking_summary";
</style>
